import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { apiClient } from '@/lib/api-client'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { ScrollText } from 'lucide-react'

export default function AgreementPage() {
  const { refreshToken, hasAcceptedCurrentTerms } = useAuth()
  const navigate = useNavigate()
  const [agreementText, setAgreementText] = useState('')
  const [agreementVersion, setAgreementVersion] = useState('')
  const [isLoading, setIsLoading] = useState(true)
  const [isAccepting, setIsAccepting] = useState(false)
  const [hasAgreed, setHasAgreed] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (hasAcceptedCurrentTerms) {
      navigate('/overview', { replace: true })
      return
    }

    const loadAgreement = async () => {
      const response = await apiClient.getAgreement()
      if (response.success && response.data) {
        setAgreementText(response.data.text)
        setAgreementVersion(response.data.version)
      } else {
        setError('Failed to load user agreement. Please try again.')
      }
      setIsLoading(false)
    }
    loadAgreement()
  }, [hasAcceptedCurrentTerms, navigate])

  const handleAccept = async () => {
    if (!hasAgreed || !agreementVersion) return

    setIsAccepting(true)
    setError(null)

    const response = await apiClient.acceptAgreement(agreementVersion)
    if (response.success) {
      await refreshToken()
      navigate('/overview', { replace: true })
    } else {
      setError(response.error || 'Failed to accept agreement. Please try again.')
      setIsAccepting(false)
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    )
  }

  return (
    <main className="mx-auto flex min-h-screen w-full max-w-4xl flex-col items-center px-4 py-12 sm:px-6 lg:px-8">
      <div className="mb-8 text-center">
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
          <ScrollText className="h-6 w-6 text-primary" />
        </div>
        <h1 className="text-3xl font-semibold tracking-tight text-foreground">
          User Agreement
        </h1>
        <p className="mt-2 text-muted-foreground">
          Please review and accept the EtherPilot User Agreement to continue.
        </p>
      </div>

      <Card className="w-full shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg">
            EtherPilot User Agreement
            <span className="ml-2 text-sm font-normal text-muted-foreground">
              v{agreementVersion}
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="max-h-[50vh] overflow-y-auto rounded-md border bg-muted/30 p-6">
            <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed text-foreground">
              {agreementText}
            </pre>
          </div>

          <div className="flex items-start space-x-3">
            <Checkbox
              id="agree"
              checked={hasAgreed}
              onCheckedChange={(checked) => setHasAgreed(checked === true)}
              disabled={isAccepting}
            />
            <Label
              htmlFor="agree"
              className="text-sm leading-relaxed cursor-pointer"
            >
              I have read and agree to the EtherPilot User Agreement
            </Label>
          </div>

          {error && (
            <p className="text-sm text-destructive">{error}</p>
          )}

          <Button
            className="w-full"
            size="lg"
            onClick={handleAccept}
            disabled={!hasAgreed || isAccepting}
          >
            {isAccepting ? 'Accepting...' : 'Accept and Continue'}
          </Button>
        </CardContent>
      </Card>
    </main>
  )
}
